/* oxlint-disable complexity */
import { useRef } from 'react';
import type React from 'react';

import { Separator } from '@change/ds-core-ui/Separator';
import { Grid } from '@change/ds-layout/Grid';
import { cn } from '@change/ds-utils';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	SignFormAddressRemovalTracking,
	SignerCarouselColorsTracking,
} from '#src/app/pages/petitionDetails/shared/components';
import {
	useFirstMovement,
	useIsEndorsementsPetitionEnabled,
	usePhoto,
	useStickyBehavior,
	PublicFieldContextProvider,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { DecisionMakerSection } from '../../containers/DecisionMakerSection';
import { EndorsementsContainer } from '../../containers/endorsements';
import { TheSupporters } from '../../containers/TheSupporters';
import {
	SupporterVoicesFeedProvider,
	useSupporterVoicesFeedContext,
} from '../../containers/TheSupporters/components/SupporterVoices/components/SupporterVoicesFeed/shared/context';
import { UpdatesContainer } from '../../containers/Updates';
import { Description } from '../Description';
import { Heading } from '../Heading';
import { HeadingMobile } from '../HeadingMobile';
import { HeadingMobileWithPhoto } from '../HeadingMobileWithPhoto';
import { HeadingWithPhoto } from '../HeadingWithPhoto';
import { MediaHits } from '../MediaHits';
import type { ActionPanelLayout } from '../MobileActionButton';
import { MobileActionButton } from '../MobileActionButton';
import { MomentumBarContent } from '../MomentumBar';
import { Movement } from '../Movement';
import { PetitionStarter } from '../PetitionStarter';
import { PetitionTimestamp } from '../PetitionTimestamp';
import { ReportAbuse } from '../ReportAbuse';
import { SapCta } from '../SapCta';
import { ScrollTrackingWaypoint } from '../ScrollTrackingWaypoint';
import { SharePetition } from '../SharePetition';
import { Side } from '../Side';
import { SignatureStatsMobile } from '../Stats';
import { TagBreadcrumbs } from '../TagBreadcrumbs';
import type { VideoFirstHeroStatus } from '../VideoFirstHero';
import { VideoFirstHero, useVideoFirstHeroStatus } from '../VideoFirstHero';

import { AfterDescriptionWaypoint } from './components/AfterDescriptionWaypoint';
import { AfterMobileActionButtonWaypoint } from './components/AfterMobileActionButtonWaypoint';
import { useVideoFirstExperiment } from './hooks/useVideoFirstExperiment';

const VideoFirstMovement: React.FC<{ hasPetitionPhoto: boolean }> = ({ hasPetitionPhoto }) => {
	const firstMovement = useFirstMovement();
	const movementPhoto = usePhoto();

	if (!firstMovement) return null;

	return (
		<div className={hasPetitionPhoto ? 'mb-4 px-4 sm:mb-2 sm:px-0' : 'mb-4 px-4 md:mb-2 md:px-0'}>
			<Movement movement={firstMovement} alwaysExpanded={!movementPhoto} />
		</div>
	);
};

/**
 * The two heading slots, which sit in different grid cells and so can't be one component. Kept out
 * of MainInner because they only depend on the petition photo and the video-first hero, and
 * inlining all seven derivations pushed that component past the max-statements ceiling.
 */
function buildHeadings({
	hasPhoto,
	videoFirstSourceUrl,
	videoFirstHeroStatus,
}: {
	hasPhoto: boolean;
	videoFirstSourceUrl: string | undefined;
	videoFirstHeroStatus: VideoFirstHeroStatus;
}): { desktopHeading: React.ReactNode; mobileHeading: React.ReactNode } {
	const desktopHeadingClassName = cn('col-span-full hidden w-full md:row-start-1 md:block', {
		'lg:col-start-2 lg:-col-end-1': !hasPhoto,
	});
	const defaultDesktopHeading = <div className={desktopHeadingClassName}>{hasPhoto ? <HeadingWithPhoto /> : null}</div>;
	const videoFirstDesktopFallback = (
		<div className={desktopHeadingClassName}>{hasPhoto ? <HeadingWithPhoto /> : <Heading />}</div>
	);

	const defaultMobileHeading = hasPhoto ? <HeadingMobileWithPhoto /> : <HeadingMobile />;
	const videoFirstMobileFallback = hasPhoto ? (
		<HeadingMobileWithPhoto showMovement={false} />
	) : (
		<HeadingMobile showMovement={false} />
	);

	const desktopHeading = videoFirstSourceUrl ? (
		<VideoFirstHero
			fallback={videoFirstDesktopFallback}
			sourceUrl={videoFirstSourceUrl}
			status={videoFirstHeroStatus}
			viewport="desktop"
		/>
	) : (
		defaultDesktopHeading
	);
	const mobileHeading = videoFirstSourceUrl ? (
		<>
			<VideoFirstHero
				fallback={videoFirstMobileFallback}
				sourceUrl={videoFirstSourceUrl}
				status={videoFirstHeroStatus}
				viewport="mobile"
			/>
			<VideoFirstMovement hasPetitionPhoto={hasPhoto} />
		</>
	) : (
		defaultMobileHeading
	);

	return { desktopHeading, mobileHeading };
}

// oxlint-disable-next-line max-lines-per-function
const MainInner: React.FC<EmptyProps> = () => {
	const {
		data: {
			petition: { id: petitionId, photo },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { showSupporterVoicesFeed },
	} = useSupporterVoicesFeedContext();
	const {
		data: {
			ownPetition,
			experiments: { signFormGdprRadioRemovalExperiment },
		},
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const canBeSticky = useStickyBehavior(signatureBlockRef);

	const isEndorsementsEnabled = useIsEndorsementsPetitionEnabled();

	const { experiment: videoFirstExperiment, hasVideoFirstSource, videoFirstSourceUrl } = useVideoFirstExperiment();
	const videoFirstHeroStatus = useVideoFirstHeroStatus(videoFirstSourceUrl);
	const usesVideoFirstHeroLayout = videoFirstHeroStatus !== 'fallback';
	// The video-first hero docks the bar from page load; every other page keeps the default
	// inline-then-sticky behaviour, which `MobileActionButton` applies for `undefined`.
	const actionPanelLayout: ActionPanelLayout | undefined = usesVideoFirstHeroLayout ? 'sticky-sign' : undefined;

	const { desktopHeading, mobileHeading } = buildHeadings({
		hasPhoto: photo !== null,
		videoFirstSourceUrl,
		videoFirstHeroStatus,
	});
	const sidePanelStartsInSecondRow = usesVideoFirstHeroLayout || (photo === null && videoFirstSourceUrl !== undefined);

	return (
		<PublicFieldContextProvider>
			{/*
			 * Outside both columns because exposure must fire in every arm, and outside the sign form: the
			 * desktop sidebar and mobile sign modal each mount their own, and exposure dedup is per mount.
			 */}
			<SignFormAddressRemovalTracking />
			<Grid className="grid-rows-[auto] px-0 lg:max-w-[1376px] lg:grid-cols-14! lg:grid-rows-[auto]">
				{desktopHeading}

				<div
					className={cn('col-span-full max-md:px-0 md:col-span-5 md:container lg:col-start-2! lg:col-end-11!', {
						'md:row-start-2': !usesVideoFirstHeroLayout && photo !== null,
						'md:row-start-3': usesVideoFirstHeroLayout,
					})}
				>
					{mobileHeading}

					<div className="p-4 md:p-0">
						<div className="hidden md:block">
							<MomentumBarContent petitionId={petitionId} />
							<Description />

							<ReportAbuse />

							{isEndorsementsEnabled && <EndorsementsContainer />}
							<PetitionStarter />
						</div>

						<div className="block md:hidden">
							<MobileActionButton layout={actionPanelLayout} sticky={false} variant="v1" />

							<AfterMobileActionButtonWaypoint />
							<SignatureStatsMobile />

							<MomentumBarContent petitionId={petitionId} />
							<Description />

							<ReportAbuse />

							{isEndorsementsEnabled && <EndorsementsContainer />}

							<PetitionStarter />
						</div>

						<ScrollTrackingWaypoint name="scroll_past_body" />
						<AfterDescriptionWaypoint />
					</div>
				</div>
				{/* We need to modify the row-span to match the exact last row before the first full width module */}
				<div
					data-qa="second-column-container"
					className={cn(
						'h-0 md:col-start-6 md:col-end-9 md:row-span-3 md:mb-20 md:h-full lg:col-start-11 lg:col-end-15',
						sidePanelStartsInSecondRow ? 'md:row-start-2' : 'md:row-start-1',
					)}
				>
					<div className="block md:hidden">
						<MobileActionButton layout={actionPanelLayout} sticky={true} variant="v1" />
					</div>
					<div
						data-qa="sticky-action-panel-container"
						className={cn(
							'relative top-6 right-0 z-12 mt-0 hidden md:mt-6! md:mr-6! md:block md:max-w-[350px] lg:mt-6! lg:mr-6 lg:max-w-[350px]',
							{
								'!sticky': canBeSticky,
							},
						)}
					>
						<div>
							<Side containerRef={signatureBlockRef} />
						</div>
					</div>
				</div>

				{/* Starters bypass assignment, so keep their forced experience out of the experiment readout. */}
				{hasVideoFirstSource && !ownPetition && (
					<TrackExperimentExposure experiment={videoFirstExperiment} resetKeys={[petitionId]} />
				)}

				{/*
				 * Desktop sidebar and mobile sign modal both mount their own sign form concurrently, so this
				 * must live here rather than inside the sign form.
				 */}
				<TrackExperimentExposure experiment={signFormGdprRadioRemovalExperiment} resetKeys={[petitionId]} />

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SapCta />

					{/* Shares this cell to preserve the body-column width without adding another Grid row gap. */}
					<MediaHits className="mb-20" />

					<DecisionMakerSection />
				</div>

				<div
					className={cn('col-span-full', {
						// Mixed feed: full-width breakout for the wide card grid.
						'my-6 px-4 md:px-0': showSupporterVoicesFeed,
						// Legacy comments/videos fallback: centered in the page grid, matching the
						// other petition-body sections (updates, share, etc.).
						'container lg:col-start-2 lg:col-end-11': !showSupporterVoicesFeed,
					})}
				>
					<TheSupporters showSupporterVoicesFeed={showSupporterVoicesFeed} />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<ScrollTrackingWaypoint name="scroll_updates_section" />

					<Separator className="mt-14 mb-8 md:mt-20" />

					<UpdatesContainer />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SharePetition />

					<Separator className="mt-14 mb-6 md:mt-20" />
					<PetitionTimestamp />
					<Separator className="my-6" />
					<TagBreadcrumbs />
				</div>
			</Grid>
		</PublicFieldContextProvider>
	);
};

/**
 * Wraps the page content in the supporter-voices feed provider, so the feed provider holds the
 * single supporter-voices feed instance and the page layout decision (showSupporterVoicesFeed,
 * read in MainInner) and the feed render (SupporterVoicesFeed, nested below) share one
 * `useSupporterVideos` state. See CHANGE-94834.
 */
export const Main: React.FC<EmptyProps> = () => {
	return (
		<SupporterVoicesFeedProvider>
			{/* EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup */}
			<SignerCarouselColorsTracking>
				<MainInner />
			</SignerCarouselColorsTracking>
		</SupporterVoicesFeedProvider>
	);
};
